import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'

interface ExportCsvAmountModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onConfirm: (amount: number) => void
	defaultAmount?: number
	maxAmount?: number
	isLoading?: boolean
}

export function ExportCsvAmountModal({
	open,
	onOpenChange,
	onConfirm,
	defaultAmount = 1000,
	maxAmount = 100000,
	isLoading = false,
}: ExportCsvAmountModalProps) {
	const [amount, setAmount] = useState<string>(defaultAmount.toString())
	const [error, setError] = useState<string>('')

	const handleConfirm = () => {
		const numAmount = parseInt(amount, 10)

		if (!amount || isNaN(numAmount)) {
			setError('กรุณากระบอกจำนวนที่ต้องการดาวน์โหลด')
			return
		}

		if (numAmount <= 0) {
			setError('จำนวนต้องมากกว่า 0')
			return
		}

		if (numAmount > maxAmount) {
			setError(`จำนวนต้องไม่เกิน ${maxAmount.toLocaleString('th-TH')} รายการ`)
			return
		}

		setError('')
		onConfirm(numAmount)
		onOpenChange(false)
		// Reset to default after closing
		setTimeout(() => setAmount(defaultAmount.toString()), 300)
	}

	const handleOpenChange = (newOpen: boolean) => {
		if (!isLoading) {
			onOpenChange(newOpen)
			if (!newOpen) {
				setError('')
				setTimeout(() => setAmount(defaultAmount.toString()), 300)
			}
		}
	}

	const handleQuickSelect = (value: number) => {
		setAmount(value.toString())
		setError('')
	}

	const presetAmounts = [100, 1000, 5000, 10000].filter(
		(preset) => preset <= maxAmount,
	)

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>ดาวน์โหลด CSV</DialogTitle>
					<DialogDescription>
						กรุณาระบุจำนวนรายการที่ต้องการดาวน์โหลด
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<div className="grid gap-2">
						<Label htmlFor="amount">จำนวนรายการ</Label>

						{/* Quick Selection Buttons */}
						<div className="flex flex-wrap gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => handleQuickSelect(defaultAmount)}
								disabled={isLoading}
								className="min-w-[80px] flex-1"
							>
								ทั้งหมด ({defaultAmount.toLocaleString('th-TH')})
							</Button>
							{presetAmounts.map((preset) => (
								<Button
									key={preset}
									type="button"
									variant="outline"
									size="sm"
									onClick={() => handleQuickSelect(preset)}
									disabled={isLoading}
									className="min-w-[70px] flex-1"
								>
									{preset.toLocaleString('th-TH')}
								</Button>
							))}
						</div>

						<Input
							id="amount"
							type="number"
							value={amount}
							onChange={(e) => {
								setAmount(e.target.value)
								setError('')
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleConfirm()
								}
							}}
							placeholder={`ระบุจำนวน (สูงสุด ${maxAmount.toLocaleString('th-TH')})`}
							min={1}
							max={maxAmount}
							disabled={isLoading}
						/>
						{error && <p className="text-sm text-destructive">{error}</p>}
						<p className="text-sm text-muted-foreground">
							สูงสุด {maxAmount.toLocaleString('th-TH')} รายการ
						</p>
					</div>
				</div>
				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={() => handleOpenChange(false)}
						disabled={isLoading}
					>
						ยกเลิก
					</Button>
					<Button type="button" onClick={handleConfirm} disabled={isLoading}>
						ดาวน์โหลด
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
